/**
 * Experiment Tracking Hook
 *
 * Reusable hook for tracking A/B experiment participation across the app.
 * Fires GTM events, PostHog exposure events, and logs experiment activity when users are assigned to variants.
 *
 * Used in _app.js to track experiment participation across all pages.
 */

import useGTM from "@/hooks/useGTM";
import { logger } from "@/src/utils/logger";
import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";

export type ExperimentConfig = {
  name: string;
  getVariant: () => string | null;
  eventName?: string; // Optional custom event name, defaults to {name}_experiment_participation
  featureFlagName?: string; // Optional PostHog feature flag name, defaults to experiment name
};

/**
 * Hook for tracking experiment participation and firing GTM events
 * Also fires PostHog exposure events for proper experiment analytics
 */
export const useExperimentTracking = (config: ExperimentConfig) => {
  const router = useRouter();
  const { pushEvent } = useGTM();
  const posthog = usePostHog();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const variant = config.getVariant();

    if (variant) {
      // Fire GTM event
      const eventName =
        config.eventName ||
        `${config.name.replace(/-/g, "_")}_experiment_participation`;
      pushEvent(eventName, {
        experiment: config.name,
        variant: variant,
      });

      // Fire PostHog exposure event
      if (posthog) {
        const featureFlagName = config.featureFlagName || config.name;
        posthog.capture("$feature_flag_called", {
          $feature_flag: featureFlagName,
          $feature_flag_response: variant,
          experiment: config.name,
        });
      }

      logger.log(
        `🧪 [${config.name.toUpperCase()}] Experiment active - variant: ${variant}, page: ${
          router.pathname
        }`
      );
    }
  }, [router.pathname, config, pushEvent, posthog]);
};
